import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import {
  collection,
  where,
  query,
  getDocs,
  getDoc,
  doc,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import {
  db,
  auth,
  messaging,
  isSupported as checkIsSupported,
} from '../../config/firebase';
import { getToken, deleteToken } from 'firebase/messaging';
import { isOnMobile } from '../../util/util';

export const isSupported = checkIsSupported;

const queryCache = new Map();

function withCache(fn, ttlMs = 10000) {
  return async (...args) => {
    const cacheKey = JSON.stringify(args);
    const now = Date.now();
    if (queryCache.has(cacheKey)) {
      const cached = queryCache.get(cacheKey);
      if (now - cached.timestamp < ttlMs) {
        return cached.promise;
      }
    }
    const promise = fn(...args).catch((err) => {
      queryCache.delete(cacheKey);
      throw err;
    });
    queryCache.set(cacheKey, { promise, timestamp: now });
    return promise;
  };
}

export const getUserData = withCache(async (uid) => {
  const userCollection = collection(db, 'User');
  const userQuery = query(userCollection, where('uid', '==', uid));
  try {
    const spanShot = await getDocs(userQuery);
    return spanShot.docs[0].data();
  } catch (err) {
    throw err;
  }
});

export const updateUserData = async (uid, update) => {
  const userCollection = collection(db, 'User');
  const userQuery = query(userCollection, where('uid', '==', uid));
  try {
    const spanShot = await getDocs(userQuery);
    return spanShot.docs[0].data();
  } catch (err) {
    throw err;
  }
};

// True when the username is already registered. Reads the public username
// Index (which holds no personal data) instead of querying User profiles,
// which are readable only by their owner.
export const checkUserNameTaken = async (username) => {
  const indexRef = doc(db, 'Index', 'User', 'username', username);
  const snapshot = await getDoc(indexRef);
  return snapshot.exists();
};

export const getUserBookMarks = withCache(async (uid) => {
  const bookMarks = collection(db, `BookMarks/${uid}/collection`);
  const bookMarkQuery = query(bookMarks);
  try {
    const spanShot = await getDocs(bookMarkQuery);
    const bookMarksList = spanShot.docs.map((item) => {
      const obj = {
        url: item.data().url,
        text: item.data().text,
        pageNo: item.data().pageNo,
        context: item.data().context,
        baseUrl: item.data().baseUrl,
        id: item.id,
        created: item.created,
        shared: false,
      };
      return obj;
    });

    return bookMarksList;
  } catch (err) {
    throw err;
  }
});
export async function checkUploadBookMarks() {
  const bookMark = JSON.parse(localStorage.getItem('bookMarks'));

  if (bookMark && bookMark.length > 0) {
    if (auth.currentUser) {
      const bookMarksCollection = collection(
        db,
        `BookMarks/${auth.currentUser.uid}/collection`
      );

      bookMark.forEach(async (bm) => {
        await addDoc(bookMarksCollection, {
          uid: auth.currentUser.uid,
          url: bm.url,
          text: bm.text,
          pageNo: bm.pageNo,
          context: bm.context,
          title: bm.title,
          baseUrl: bm.baseUrl,
          created: serverTimestamp(),
          shared: false,
        });
      });
      localStorage.removeItem('bookMarks');
    } else {
      console.log('BookMark cannot be saved');
    }
  }
}

export async function checkUploadFCMToken() {
  const token = localStorage.getItem('notificationToken');

  if (token && token.length > 0) {
    if (auth.currentUser) {
      const tokenCollection = collection(
        db,
        `FCMTokens/${auth.currentUser.uid}/collection`
      );
      try {
        const docRef = await addDoc(tokenCollection, {
          uid: auth.currentUser.uid,
          token: token,
          isMobile: isOnMobile(),
          userAgent: navigator.userAgent,
          topics: 'default',
          created: serverTimestamp(),
        });
        if (!docRef) {
          console.log('Failed to store FCM token');
          return;
        }
      } catch (err) {
        console.log('error in storing FCM Tokens');
        return;
      }
      const currentTimestamp = new Date().getTime();
      localStorage.setItem('FCMtokenUploadedToServer', currentTimestamp);
    } else {
      console.log('Token cannot be saved');
    }
  }
}

export const generateToken = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      let permStatus = await PushNotifications.checkPermissions();
      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }
      if (permStatus.receive !== 'granted') {
        console.log('User denied native push permission');
        return;
      }

      await PushNotifications.register();

      PushNotifications.addListener('registration', (token) => {
        const notificationToken = token.value;
        const storedToken = localStorage.getItem('notificationToken');

        if (storedToken && storedToken.length > 0) {
          if (storedToken === notificationToken) {
            return;
          }
        }

        localStorage.setItem('notificationToken', notificationToken);
        checkUploadFCMToken();
      });

      PushNotifications.addListener('registrationError', (error) => {
        console.error(
          'Error on native push registration: ' + JSON.stringify(error)
        );
      });
    } catch (err) {
      console.error('Native Push Notification error', err);
    }
    return;
  }

  const support = isSupported();
  if (support && messaging) {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      let registration;

      // Only wait for SW ready if we expect one (e.g. Production or if controller exists)
      if (
        process.env.NODE_ENV === 'production' ||
        navigator.serviceWorker.controller
      ) {
        try {
          registration = await navigator.serviceWorker.ready;
        } catch (err) {
          console.error('Service Worker registration failed', err);
        }
      }

      try {
        const notificationToken = await getToken(messaging, {
          vapidKey:
            'BNkNTJdM7j29aXLXLH9NqxjP4KIFsqXctf5sssl_N4wgwAGFBle0zeH1O4Ob91TQDcbQGd6mijMiu7kugrWX-rE',
          serviceWorkerRegistration: registration,
        });
        const token = localStorage.getItem('notificationToken');

        if (token && token.length > 0) {
          if (token === notificationToken) {
            return;
          }
        }

        localStorage.setItem('notificationToken', notificationToken);
        checkUploadFCMToken();
      } catch (error) {
        console.log('An error occurred while retrieving token. ', error);
      }
    } else if (permission === 'denied') {
      alert(
        'You have denied the permission to notifications,allow it in Browsers Settings'
      );
    }
  } else {
    console.log('Notifications not supported or messaging not initialized');
  }
};

export const deleteFCMToken = async () => {
  if (messaging) {
    try {
      await deleteToken(messaging);
      localStorage.removeItem('notificationToken');
      console.log('Token deleted.');
      return true;
    } catch (error) {
      console.log('Unable to delete token', error);
      return false;
    }
  }
  return false;
};
